// Stock control for the Admin (M4): the approvals inbox, counts, write-offs and their
// report, the daily expiry list and the stock-control settings. URL search params, API calls
// and TanStack Query options; every response is parsed with the shared @enp/domain/api schema.

import {
  type ApprovalDecisionRequest,
  type UpdateStockControlSettings,
  type WriteOff,
  type WriteOffsResponse,
  approvalDecisionResponseSchema,
  approvalSchema,
  approvalsResponseSchema,
  countDetailSchema,
  countsResponseSchema,
  STATE_TASK_STATUSES,
  STOCK_PAGE_MAX,
  stateListGenerateResponseSchema,
  stateTasksResponseSchema,
  stockControlSettingsSchema,
  WRITE_OFF_REPORT_GROUPS,
  writeOffReportSchema,
  writeOffsResponseSchema,
} from '@enp/domain/api';
import { WRITE_OFF_REASONS } from '@enp/domain/stock-control';
import { isPlainDate } from '@enp/domain/time';
import { infiniteQueryOptions, queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';
import { clean, orderedRange, PAGE_SIZE } from '../stock/queries.js';
import { INBOX_VIEWS, type InboxView } from './words.js';

const plainDate = z.string().refine(isPlainDate);
const optionalDay = plainDate.optional().catch(undefined);

export const stockControlKeys = {
  all: ['stock-control'] as const,
  approvals: (view: InboxView) => ['stock-control', 'approvals', view] as const,
  approval: (id: string) => ['stock-control', 'approval', id] as const,
  counts: (search: CountsSearch) => ['stock-control', 'counts', clean(search)] as const,
  count: (id: string) => ['stock-control', 'count', id] as const,
  writeOffs: (search: WriteOffsSearch) => ['stock-control', 'write-offs', clean(search)] as const,
  report: (search: WriteOffsSearch, from: string, to: string) =>
    ['stock-control', 'write-off-report', clean(search), from, to] as const,
  tasks: (day: string | undefined) => ['stock-control', 'state-tasks', day ?? 'today'] as const,
  settings: () => ['stock-control', 'settings'] as const,
};

/** After a decision or a change: everything stock control shows is read again. */
export const invalidateStockControl = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: stockControlKeys.all }),
    // A rejection writes movements: the stock screens change too.
    queryClient.invalidateQueries({ queryKey: ['stock'] }),
  ]);

// ---------------------------------------------------------------- approvals

/** The inbox's state in the URL: which view, and which request is open. */
export const approvalsSearchSchema = z.object({
  view: z.enum(INBOX_VIEWS).optional().catch(undefined),
  id: z.uuid().optional().catch(undefined),
});
export type ApprovalsSearch = z.infer<typeof approvalsSearchSchema>;

export const approvalsQuery = (view: InboxView) =>
  infiniteQueryOptions({
    queryKey: stockControlKeys.approvals(view),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ status: view, limit: String(PAGE_SIZE) });
      if (pageParam) params.set('cursor', pageParam);
      return api(`/approvals?${params.toString()}`, { signal, schema: approvalsResponseSchema });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

/**
 * How many requests wait for a decision (UI4A-4): the nav item and the Waiting tab. Up to
 * one page of the largest size; `more` when there are more than that.
 */
export const waitingApprovalsQuery = () =>
  queryOptions({
    queryKey: [...stockControlKeys.all, 'waiting-count'] as const,
    queryFn: async ({ signal }) => {
      const page = await api(`/approvals?status=requested&limit=${STOCK_PAGE_MAX}`, {
        signal,
        schema: approvalsResponseSchema,
      });
      return { count: page.items.length, more: page.next_cursor !== null };
    },
    retry: false,
    staleTime: 30_000,
  });

/** "3", "200+": a waiting count as the nav and the tab show it. */
export const waitingText = (w: { count: number; more: boolean }) =>
  `${w.count}${w.more ? '+' : ''}`;

export const approvalQuery = (id: string) =>
  queryOptions({
    queryKey: stockControlKeys.approval(id),
    queryFn: ({ signal }) =>
      api(`/approvals/${encodeURIComponent(id)}`, { signal, schema: approvalSchema }),
    retry: false,
  });

export const decideApproval = (id: string, body: ApprovalDecisionRequest) =>
  api(`/approvals/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    body,
    schema: approvalDecisionResponseSchema,
  });

// ---------------------------------------------------------------- counts

/** The counts list's filters in the URL. */
export const countsSearchSchema = z.object({
  from: optionalDay,
  to: optionalDay,
  location: z.uuid().optional().catch(undefined),
  product: z.uuid().optional().catch(undefined),
  /** Only counts with an adjustment above the threshold. */
  flagged: z.literal(true).optional().catch(undefined),
});
export type CountsSearch = z.infer<typeof countsSearchSchema>;

export function countsQueryString(search: CountsSearch, cursor: string | null): string {
  const s = orderedRange(search);
  const params = new URLSearchParams();
  if (s.from) params.set('from', s.from);
  if (s.to) params.set('to', s.to);
  if (s.location) params.set('location', s.location);
  if (s.product) params.set('product', s.product);
  if (s.flagged) params.set('needs_approval', 'true');
  params.set('limit', String(PAGE_SIZE));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export const countsQuery = (search: CountsSearch) =>
  infiniteQueryOptions({
    queryKey: stockControlKeys.counts(search),
    queryFn: ({ pageParam, signal }) =>
      api(`/counts?${countsQueryString(search, pageParam)}`, {
        signal,
        schema: countsResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

export const countQuery = (id: string) =>
  queryOptions({
    queryKey: stockControlKeys.count(id),
    queryFn: ({ signal }) =>
      api(`/counts/${encodeURIComponent(id)}`, { signal, schema: countDetailSchema }),
    retry: false,
  });

// ---------------------------------------------------------------- write-offs

/** The write-offs screen's state in the URL: the period, the grouping and the filters. */
export const writeOffsSearchSchema = z.object({
  from: optionalDay,
  to: optionalDay,
  group: z.enum(WRITE_OFF_REPORT_GROUPS).optional().catch(undefined),
  reason: z.enum(WRITE_OFF_REASONS).optional().catch(undefined),
  product: z.uuid().optional().catch(undefined),
  status: z.enum(['applied', 'reversed']).optional().catch(undefined),
});
export type WriteOffsSearch = z.infer<typeof writeOffsSearchSchema>;

export function writeOffsQueryString(
  search: WriteOffsSearch,
  from: string,
  to: string,
  cursor: string | null,
  limit = PAGE_SIZE,
): string {
  const params = new URLSearchParams({ from, to });
  if (search.reason) params.set('reason', search.reason);
  if (search.product) params.set('product', search.product);
  if (search.status) params.set('status', search.status);
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export const writeOffsQuery = (search: WriteOffsSearch, from: string, to: string) =>
  infiniteQueryOptions({
    queryKey: [...stockControlKeys.writeOffs(search), from, to] as const,
    queryFn: ({ pageParam, signal }) =>
      api(`/write-offs?${writeOffsQueryString(search, from, to, pageParam)}`, {
        signal,
        schema: writeOffsResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

export const writeOffReportQuery = (search: WriteOffsSearch, from: string, to: string) =>
  queryOptions({
    queryKey: stockControlKeys.report(search, from, to),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ from, to, group_by: search.group ?? 'reason' });
      return api(`/write-offs/report?${params.toString()}`, {
        signal,
        schema: writeOffReportSchema,
      });
    },
    retry: false,
  });

/** Every write-off of the period and filters (for the CSV), page by page, up to `max`. */
export async function allWriteOffs(
  search: WriteOffsSearch,
  from: string,
  to: string,
  max = 10_000,
): Promise<{ items: WriteOff[]; truncated: boolean }> {
  const items: WriteOff[] = [];
  let cursor: string | null = null;
  do {
    const page: WriteOffsResponse = await api(
      `/write-offs?${writeOffsQueryString(search, from, to, cursor, 200)}`,
      {
        schema: writeOffsResponseSchema,
      },
    );
    items.push(...page.items);
    cursor = page.next_cursor;
  } while (cursor && items.length < max);
  return { items, truncated: cursor !== null };
}

/** The period a write-offs search covers: its own days, else the last 7 days to today. */
export function writeOffPeriod(
  search: Pick<WriteOffsSearch, 'from' | 'to'>,
  defaultFrom: string,
  today: string,
): { from: string; to: string } {
  const s = orderedRange({ from: search.from ?? defaultFrom, to: search.to ?? today });
  return { from: s.from, to: s.to };
}

// ---------------------------------------------------------------- the daily expiry list

/** The expiry list's state in the URL: the day, the branch, the status and one lot. */
export const expirySearchSchema = z.object({
  day: optionalDay,
  branch: z.uuid().optional().catch(undefined),
  status: z.enum(STATE_TASK_STATUSES).optional().catch(undefined),
  lot: z.uuid().optional().catch(undefined),
  /** Only moves back to an earlier state (a person decides them). */
  backward: z.literal(true).optional().catch(undefined),
});
export type ExpirySearch = z.infer<typeof expirySearchSchema>;

export const stateTasksQuery = (day: string | undefined) =>
  queryOptions({
    queryKey: stockControlKeys.tasks(day),
    queryFn: ({ signal }) =>
      api(`/state-tasks${day ? `?day=${day}` : ''}`, { signal, schema: stateTasksResponseSchema }),
    retry: false,
  });

export const generateStateList = (branchId: string) =>
  api('/state-tasks/generate', {
    method: 'POST',
    body: { branch_id: branchId },
    schema: stateListGenerateResponseSchema,
  });

// ---------------------------------------------------------------- settings

export const stockControlSettingsQuery = () =>
  queryOptions({
    queryKey: stockControlKeys.settings(),
    queryFn: ({ signal }) =>
      api('/settings/stock-control', { signal, schema: stockControlSettingsSchema }),
    retry: false,
  });

export const updateStockControlSettings = (body: UpdateStockControlSettings) =>
  api('/settings/stock-control', { method: 'PUT', body, schema: stockControlSettingsSchema });
